/**
 * AdminFormBuilder — create/edit a JCC form template (Aug 2026 native
 * Form Builder). Reuses FieldEditorRow / FormFieldRenderer /
 * StandardBlockRenderer so the exact same rendering logic is used
 * here (Preview tab), in the member Fill page, and in Print pages.
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Eye, Pencil } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { PageHero } from "../../components/shared/Primitives";
import { FIELD_TYPES, STANDARD_BLOCK_FIELDS, blankField } from "../../lib/formFieldTypes";
import { toEditableHtml } from "../../lib/formsRichText";
import { FieldEditorRow } from "../../components/forms/FieldEditorRow";
import { FormFieldRenderer } from "../../components/forms/FormFieldRenderer";
import { StandardBlockRenderer } from "../../components/forms/StandardBlockRenderer";
import { FormsRichTextEditor } from "../../components/forms/FormsRichTextEditor";
import { RichTextDisplay } from "../../components/forms/RichTextDisplay";

const inputCls = "mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal/30";

const ENTITLEMENT_OPTIONS = [
  { value: "carer_free", label: "Free Carer" },
  { value: "carer_essentials", label: "Carer Essentials" },
  { value: "pro_carer", label: "Pro Carer Office" },
  { value: "family_free", label: "Free Family" },
  { value: "family_circle", label: "Family Care Office" },
];

function defaultStandardBlock() {
  return {
    enabled: false,
    fields: STANDARD_BLOCK_FIELDS.map((f) => ({ key: f.key, enabled: false, required: false, label: f.defaultLabel })),
  };
}

export default function AdminFormBuilder() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const isEditing = !!templateId;

  const [loaded, setLoaded] = useState(!isEditing);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("editor"); // editor | preview | print

  const [status, setStatus] = useState("draft");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [displayOrder, setDisplayOrder] = useState(0);
  const [entitlements, setEntitlements] = useState([]);
  const [introText, setIntroText] = useState("");
  const [standardBlock, setStandardBlock] = useState(defaultStandardBlock());
  const [fields, setFields] = useState([]);
  const [previewAnswers, setPreviewAnswers] = useState({});
  const [previewStandardAnswers, setPreviewStandardAnswers] = useState({});

  useEffect(() => {
    if (!isEditing) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/api/admin/forms/${templateId}`);
        if (!alive) return;
        setStatus(data.status);
        setTitle(data.title);
        setDescription(toEditableHtml(data.description));
        setCategory(data.category);
        setDisplayOrder(data.display_order);
        setEntitlements(data.entitlements || []);
        setIntroText(toEditableHtml(data.intro_text || ""));
        setStandardBlock(data.standard_block || defaultStandardBlock());
        setFields((data.fields || []).map((f) => (f.type === "text_block" ? { ...f, help_text: toEditableHtml(f.help_text) } : f)));
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Could not load this form.");
      } finally {
        if (alive) setLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [isEditing, templateId]);

  const toggleEntitlement = (val) =>
    setEntitlements((prev) => (prev.includes(val) ? prev.filter((e) => e !== val) : [...prev, val]));

  const toggleStandardField = (key, patch) =>
    setStandardBlock((prev) => ({
      ...prev,
      fields: prev.fields.map((f) => (f.key === key ? { ...f, ...patch } : f)),
    }));

  const addField = (type) => setFields((prev) => [...prev, blankField(type)]);
  const updateField = (idx, nf) => setFields((prev) => prev.map((f, i) => (i === idx ? nf : f)));
  const removeField = (idx) => setFields((prev) => prev.filter((_, i) => i !== idx));
  const duplicateField = (idx) =>
    setFields((prev) => {
      const copy = { ...prev[idx], field_id: `f_${Math.random().toString(36).slice(2, 10)}` };
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
  const moveField = (from, to) =>
    setFields((prev) => {
      const arr = [...prev];
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      return arr;
    });

  const buildPayload = () => ({
    title, description, category, display_order: Number(displayOrder) || 0,
    entitlements, intro_text: introText, standard_block: standardBlock, fields,
  });

  const save = async () => {
    if (!title.trim()) {
      toast.error("Please give the form a title.");
      return;
    }
    setSaving(true);
    try {
      if (isEditing) {
        await api.put(`/api/admin/forms/${templateId}`, buildPayload());
        toast.success("Form saved.");
      } else {
        const { data } = await api.post("/api/admin/forms", buildPayload());
        toast.success("Form created as a draft.");
        navigate(`/admin/forms/${data.template_id}/edit`);
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not save this form.");
    } finally {
      setSaving(false);
    }
  };

  const runStatusAction = async (action) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/api/admin/forms/${templateId}/${action}`);
      setStatus(data.status);
      toast.success(`Form ${action}ed.`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Action failed.");
    } finally {
      setSaving(false);
    }
  };

  if (isEditing && !loaded) {
    return <div className="container-jcc py-24 text-center text-ink-muted" data-testid="admin-form-builder-loading">Loading form…</div>;
  }

  return (
    <div data-testid="admin-form-builder-page">
      <PageHero
        eyebrow="JCC Admin"
        title={isEditing ? "Edit form" : "New form"}
        description="Build the form with sections, guidance text and fields, then preview and publish when ready."
      >
        <Link to="/admin/forms" className="btn-outline text-sm" data-testid="admin-form-builder-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to Forms
        </Link>
      </PageHero>

      <section className="section pt-8 pb-24">
        <div className="container-jcc max-w-3xl">
          {isEditing && (
            <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="admin-form-builder-status-actions">
              <span className={`pill text-xs font-semibold ${status === "published" ? "bg-green-100 text-green-800" : "bg-slate-200 text-ink"}`}>
                {status}
              </span>
              {status !== "published" && status !== "archived" && (
                <button disabled={saving} onClick={() => runStatusAction("publish")} className="btn-teal text-xs py-1.5" data-testid="admin-form-builder-publish-btn">Publish</button>
              )}
              {status === "published" && (
                <button disabled={saving} onClick={() => runStatusAction("unpublish")} className="btn-outline text-xs py-1.5" data-testid="admin-form-builder-unpublish-btn">Unpublish</button>
              )}
              {status !== "archived" ? (
                <button disabled={saving} onClick={() => runStatusAction("archive")} className="btn-outline text-xs py-1.5" data-testid="admin-form-builder-archive-btn">Archive</button>
              ) : (
                <button disabled={saving} onClick={() => runStatusAction("unarchive")} className="btn-outline text-xs py-1.5" data-testid="admin-form-builder-unarchive-btn">Restore to draft</button>
              )}
            </div>
          )}

          <div className="flex items-center gap-2 mb-6 border-b border-slate-100" data-testid="admin-form-builder-tabs">
            {[["editor", "Editor", Pencil], ["preview", "Member preview", Eye], ["print", "Print preview", Eye]].map(([val, label, Icon]) => (
              <button key={val} type="button" onClick={() => setTab(val)}
                      className={`px-3 py-2 text-sm font-medium flex items-center gap-1.5 border-b-2 -mb-px ${tab === val ? "border-teal text-teal" : "border-transparent text-ink-muted"}`}
                      data-testid={`admin-form-builder-tab-${val}`}>
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>

          {tab === "editor" && (
            <div className="space-y-6">
              <div className="card-base p-6 space-y-4">
                <label className="block">
                  <span className="text-sm font-medium text-ink">Title</span>
                  <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} data-testid="admin-form-title-input" />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-ink">Description</span>
                  <FormsRichTextEditor value={description} onChange={setDescription} testId="admin-form-description-input" />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-ink">Introductory instructions (shown before the questions)</span>
                  <FormsRichTextEditor value={introText} onChange={setIntroText} testId="admin-form-intro-input" />
                </label>
                <div className="grid sm:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-sm font-medium text-ink">Category</span>
                    <input value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls} data-testid="admin-form-category-input" />
                  </label>
                  <label className="block">
                    <span className="text-sm font-medium text-ink">Display order</span>
                    <input type="number" value={displayOrder} onChange={(e) => setDisplayOrder(e.target.value)} className={inputCls} data-testid="admin-form-display-order-input" />
                  </label>
                </div>
                <div>
                  <span className="text-sm font-medium text-ink">Visible to (membership entitlements)</span>
                  <div className="mt-2 flex flex-wrap gap-3">
                    {ENTITLEMENT_OPTIONS.map((e) => (
                      <label key={e.value} className="flex items-center gap-1.5 text-sm">
                        <input type="checkbox" checked={entitlements.includes(e.value)} onChange={() => toggleEntitlement(e.value)}
                               data-testid={`admin-form-entitlement-${e.value}`} />
                        {e.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="card-base p-6">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="text-sm font-medium text-ink">Standard information block</span>
                    <p className="text-xs text-ink-soft">Optional shared fields (carer name, client name, date…) — none are compulsory unless you tick "Required".</p>
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={standardBlock.enabled}
                           onChange={(e) => setStandardBlock((prev) => ({ ...prev, enabled: e.target.checked }))}
                           data-testid="admin-form-standard-block-enabled" />
                    Include
                  </label>
                </div>
                {standardBlock.enabled && (
                  <div className="space-y-2">
                    {standardBlock.fields.map((f) => (
                      <div key={f.key} className="flex flex-wrap items-center gap-3 border-t border-slate-100 pt-2" data-testid={`admin-form-standard-field-${f.key}`}>
                        <label className="flex items-center gap-1.5 text-sm w-40 shrink-0">
                          <input type="checkbox" checked={f.enabled} onChange={(e) => toggleStandardField(f.key, { enabled: e.target.checked })}
                                 data-testid={`admin-form-standard-field-${f.key}-enabled`} />
                          {f.key}
                        </label>
                        <input value={f.label} disabled={!f.enabled} onChange={(e) => toggleStandardField(f.key, { label: e.target.value })}
                               className={`${inputCls} mt-0 flex-1 min-w-[10rem]`} data-testid={`admin-form-standard-field-${f.key}-label`} />
                        <label className="flex items-center gap-1.5 text-xs text-ink-muted shrink-0">
                          <input type="checkbox" checked={f.required} disabled={!f.enabled} onChange={(e) => toggleStandardField(f.key, { required: e.target.checked })}
                                 data-testid={`admin-form-standard-field-${f.key}-required`} />
                          Required
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-ink">Fields</span>
                </div>
                {fields.map((field, idx) => (
                  <FieldEditorRow
                    key={field.field_id}
                    field={field}
                    index={idx}
                    total={fields.length}
                    onUpdate={(nf) => updateField(idx, nf)}
                    onRemove={() => removeField(idx)}
                    onDuplicate={() => duplicateField(idx)}
                    onMove={moveField}
                  />
                ))}
                <div className="flex flex-wrap gap-2 pt-2">
                  {FIELD_TYPES.map((t) => (
                    <button key={t.value} type="button" onClick={() => addField(t.value)}
                            className="btn-outline text-xs py-1.5" data-testid={`admin-form-add-field-${t.value}`}>
                      <Plus className="w-3.5 h-3.5" /> {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <button type="button" onClick={save} disabled={saving} className="btn-teal w-full justify-center" data-testid="admin-form-save-btn">
                {saving ? "Saving…" : isEditing ? "Save changes" : "Create draft"}
              </button>
            </div>
          )}

          {tab === "preview" && (
            <div className="card-base p-6 sm:p-8" data-testid="admin-form-member-preview">
              <h2 className="font-display text-xl font-semibold">{title || "Untitled form"}</h2>
              <RichTextDisplay content={description} className="mt-1 text-sm text-ink-muted" testId="admin-form-preview-description" />
              {introText && (
                <div className="mt-3 text-sm bg-slate-50 border border-slate-100 rounded-lg p-3">
                  <RichTextDisplay content={introText} testId="admin-form-preview-intro" />
                </div>
              )}
              <div className="mt-5">
                <StandardBlockRenderer standardBlock={standardBlock} answers={previewStandardAnswers}
                                       onChange={(k, v) => setPreviewStandardAnswers((p) => ({ ...p, [k]: v }))} mode="fill" />
                <div className="space-y-4">
                  {fields.map((field) => (
                    <FormFieldRenderer key={field.field_id} field={field} value={previewAnswers[field.field_id]}
                                        onChange={(v) => setPreviewAnswers((p) => ({ ...p, [field.field_id]: v }))} mode="fill" />
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "print" && (
            <div className="card-base p-8 bg-white" data-testid="admin-form-print-preview">
              <h2 className="font-display text-xl font-semibold">{title || "Untitled form"}</h2>
              <RichTextDisplay content={description} className="mt-1 text-sm text-ink-muted" testId="admin-form-print-description" />
              <div className="mt-5">
                <StandardBlockRenderer standardBlock={standardBlock} answers={{}} mode="print" />
                {fields.map((field) => <FormFieldRenderer key={field.field_id} field={field} value={undefined} mode="print" />)}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
